import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Shield,
    Zap,
    Headphones,
    Globe,
    BarChart,
    ArrowRight,
    Building2,
    Cpu,
    Workflow
} from "lucide-react";
import SEO from "../components/SEO";

export default function Enterprise() {
    const enterpriseSolutions = [
        {
            icon: <Cpu className="w-8 h-8 text-blue-500" />,
            title: "Custom AI Models",
            desc: "Train our AI on your specific business data for bespoke insights and automation tailored to your unique workflows."
        },
        {
            icon: <Building2 className="w-8 h-8 text-emerald-500" />,
            title: "Multi-Entity Management",
            desc: "Centralize operations across multiple subsidiaries, regions, or franchises with unified reporting and global controls."
        },
        {
            icon: <Workflow className="w-8 h-8 text-purple-500" />,
            title: "Advanced API Access",
            desc: "Full access to our RESTful APIs and Webhooks to integrate blinqbooks seamlessly into your existing tech stack."
        },
        {
            icon: <Headphones className="w-8 h-8 text-amber-500" />,
            title: "Dedicated TAM",
            desc: "Get a dedicated Technical Account Manager to assist with implementation, training, and strategic optimization."
        },
        {
            icon: <Shield className="w-8 h-8 text-rose-500" />,
            title: "Enterprise Security",
            desc: "Custom SSO/SAML integration, advanced audit logs, and role-based access control (RBAC) at scale."
        },
        {
            icon: <BarChart className="w-8 h-8 text-cyan-500" />,
            title: "Consolidated Analytics",
            desc: "Deep-dive data lakes and custom BI dashboards that bring all your enterprise data into a single source of truth."
        }
    ];

    const steps = [
        { title: "Discovery", desc: "We analyze your current infrastructure and identify automation gaps." },
        { title: "Migration", desc: "Seamless transition of your historical data with zero downtime." },
        { title: "Training", desc: "Onboarding for your entire team with personalized workshops." },
        { title: "Optimization", desc: "Continuous improvement cycles to maximize your ROI." }
    ];

    return (
        <div className="min-h-screen bg-base transition-colors duration-300">
            <SEO
                title="Enterprise Solutions"
                description="Scale your large organization with blinqbooks Enterprise. Custom AI, dedicated support, and multi-entity management designed for high-growth firms."
            />

            {/* Hero Section */}
            <section className="relative py-24 overflow-hidden bg-base text-primary">
                <div className="absolute inset-0 z-0 opacity-10 dark:opacity-20">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-500 via-transparent to-transparent"></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 dark:bg-white/10 text-blue-600 dark:text-blue-300 text-xs font-bold uppercase mb-8">
                                <Zap className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                                Built for Scale
                            </div>
                            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                                Enterprise <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500 dark:from-blue-400 dark:via-cyan-300 dark:to-emerald-400 text-glow">Intelligence.</span>
                            </h1>
                            <p className="text-xl text-secondary dark:text-blue-100/80 mb-10 leading-relaxed max-w-xl">
                                Replace fragmented legacy systems with a unified AI core. Designed for complex organizations that demand speed, security, and absolute precision.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/contact" className="btn-primary text-center">
                                    Talk to Sales
                                </Link>
                                <Link to="/features" className="btn-secondary text-center">
                                    View Feature Set
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8 }}
                            className="hidden lg:block relative"
                        >
                            <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl overflow-hidden">
                                <div className="flex items-center justify-between mb-12">
                                    <div className="flex gap-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    </div>
                                    <div className="text-xs text-secondary dark:text-blue-300 font-mono">Global Command Center</div>
                                </div>
                                <div className="space-y-6">
                                    {[75, 92, 64].map((w, i) => (
                                        <div key={i} className="space-y-2">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-secondary">Processing Stream #{i + 1}</span>
                                                <span className="text-emerald-500">{w}% Efficiency</span>
                                            </div>
                                            <div className="h-2 w-full bg-slate-500/10 dark:bg-white/5 rounded-full overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${w}%` }}
                                                    transition={{ duration: 1.5, delay: 0.5 + (i * 0.2) }}
                                                    className="h-full bg-gradient-to-r from-blue-500 to-cyan-400"
                                                ></motion.div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-12 grid grid-cols-2 gap-4">
                                    <div className="h-32 rounded-xl bg-slate-500/10 dark:bg-white/5 flex items-center justify-center">
                                        <BarChart className="w-12 h-12 text-blue-500" />
                                    </div>
                                    <div className="h-32 rounded-xl bg-slate-500/10 dark:bg-white/5 flex items-center justify-center">
                                        <Globe className="w-12 h-12 text-emerald-500" />
                                    </div>
                                </div>
                            </div>
                            {/* Abstract Glows */}
                            <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-500/30 rounded-full blur-[100px] pointer-events-none"></div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Solutions Grid */}
            <section className="py-24 bg-surface px-4 transition-colors duration-300">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-primary leading-tight">Beyond a Platform. <br />A Strategic Advantage.</h2>
                        <p className="text-secondary max-w-2xl mx-auto">Enterprise requires more than features. It requires a partner that understands the nuances of complex operations.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {enterpriseSolutions.map((sol, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="card card-hover group"
                            >
                                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                                    {sol.icon}
                                </div>
                                <h3 className="text-2xl font-bold mb-4 text-primary">{sol.title}</h3>
                                <p className="text-secondary leading-relaxed">{sol.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Implementation Roadmap */}
            <section className="py-24 bg-base text-primary relative overflow-hidden transition-colors duration-300">
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <h2 className="text-3xl md:text-5xl font-bold mb-20 text-center">Seamless Implementation</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {steps.map((step, i) => (
                            <div key={i} className="relative group p-6 rounded-2xl hover:bg-slate-500/[0.04] dark:hover:bg-white/[0.02] transition-all duration-300">
                                <div className="text-6xl font-black text-secondary/30 mb-6 group-hover:text-accent/30 transition-colors">0{i + 1}</div>
                                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                                <p className="text-secondary text-sm leading-relaxed">{step.desc}</p>
                                {i < 3 && (
                                    <div className="hidden md:block absolute top-1/2 -right-8 w-16 h-px bg-slate-200/50 dark:bg-slate-800/50"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Custom CTA */}
            <section className="py-24 px-4 bg-surface transition-colors duration-300">
                <div className="max-w-5xl mx-auto bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-2xl sm:rounded-3xl md:rounded-[3rem] p-6 sm:p-12 md:p-20 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 pointer-events-none"></div>
                    <Building2 className="w-16 h-16 text-accent mx-auto mb-8 opacity-40" />
                    <h2 className="text-3xl sm:text-4xl md:text-6xl font-black mb-6 md:mb-8 text-primary leading-tight">Ready to Modernize?</h2>
                    <p className="text-secondary mb-8 md:mb-12 text-lg sm:text-xl max-w-2xl mx-auto">
                        Speak with our enterprise architects today to learn how blinqbooks can transform your business.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
                        <Link to="/contact" className="btn-primary flex items-center justify-center gap-3 w-full sm:w-fit mx-auto text-center">
                            Connect with Sales <ArrowRight className="w-6 h-6" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
